import {
  BillingPrice,
  BillingPriceInterval,
  BillingProduct,
} from '@tribeplatform/gql-client/types'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Modal } from '@tribeplatform/react-ui-kit/Modal'

interface Props {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  isLoading?: boolean
  newProduct: BillingProduct
  price: BillingPrice
}

/**
 * Shown before a trialing network is sent to Stripe Checkout.
 *
 * Subscribing cancels the trial first — Stripe Checkout will not sell to a customer who
 * already has an active subscription, so the cancellation is unavoidable rather than a
 * choice. It is also **not** currently recoverable: nothing restores the trial if the
 * customer abandons the payment page (TK-1110). Until that changes, this dialog is what
 * stops the cancellation being a surprise, so the wording has to be explicit about it.
 */
export const TrialConversionConfirmModal = ({
  open,
  onClose,
  onConfirm,
  isLoading,
  newProduct,
  price,
}: Props) => {
  const { $t } = useI18n()

  const amount =
    typeof price?.amount === 'number'
      ? new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
        }).format(price.amount / 100)
      : undefined

  const cadence =
    price?.interval === BillingPriceInterval.Year
      ? $t({
          defaultMessage: 'billed annually',
          id: 'Admin.Billing.Convert.BilledAnnually',
        })
      : $t({
          defaultMessage: 'billed monthly',
          id: 'Admin.Billing.Convert.BilledMonthly',
        })

  return (
    <Modal open={open} onClose={onClose}>
      <Modal.Header
        title={$t(
          {
            defaultMessage: 'Subscribe to {plan}',
            id: 'Admin.Billing.Convert.Title',
          },
          { plan: newProduct?.name },
        )}
        description={amount ? `${amount} · ${cadence}` : cadence}
      />
      <Modal.Content>
        <p>
          <T
            defaultMessage="Continuing ends your free trial right away, and any remaining trial days won't carry over. Stripe opens in a new tab to complete payment."
            id="Admin.Billing.Convert.Body"
          />
        </p>
        <p className="mt-3">
          <T
            defaultMessage="Your paid plan starts once payment goes through. If you don't complete payment, the trial won't resume and your community will be unavailable until you subscribe."
            id="Admin.Billing.Convert.Warning"
          />
        </p>
      </Modal.Content>
      <Modal.Actions>
        <Button variant="primary" onClick={onConfirm} disabled={isLoading}>
          <T defaultMessage="Proceed" id="Admin.Billing.Convert.Confirm" />
        </Button>
        <Button
          variant="secondaryNeutral"
          onClick={onClose}
          disabled={isLoading}
        >
          <T defaultMessage="Keep my trial" id="Admin.Billing.Convert.Cancel" />
        </Button>
      </Modal.Actions>
    </Modal>
  )
}
